import Checkbox from "components-redesign/Checkbox/Checkbox";
import { Tooltip } from "components-redesign/Tooltip/Tooltip";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DatagridColumn } from "./types";
interface UseRowSelectionProps {
  rowSelection: boolean;
  data: any[];
  loading?: boolean;
  onSelectionChange?: (selectedRows: Set<string>) => void;
  selectedRows?: Set<string>;
  allowSelectAll?: boolean;
  getRowId?: (row: any) => string;
  checkboxExtraProps?: any;
  header?: string | React.ReactNode;
  visibleRows?: any[];
}

interface UseRowSelectionReturn {
  selectedRows: Set<string>;
  handleRowSelectionChange: (rowId: string, selected: boolean) => void;
  selectionColumn: DatagridColumn | null;
}

export const useRowSelection = ({
  rowSelection,
  data,
  loading,
  onSelectionChange,
  selectedRows,
  allowSelectAll = false,
  getRowId = (row) => row.id || row.key || JSON.stringify(row),
  checkboxExtraProps = {},
  header,
  visibleRows,
}: UseRowSelectionProps): UseRowSelectionReturn => {
  const rowsForSelectAll = visibleRows ?? data;

  // Memoize visible row IDs for efficient lookups
  const visibleRowIds = useMemo(() => {
    return new Set(rowsForSelectAll.map((row) => getRowId(row)));
  }, [rowsForSelectAll, getRowId]);

  const [selectedRowsLocal, setSelectedRowsLocal] = useState<Set<string>>(
    selectedRows ?? new Set()
  );
  const selectedRowsRef = useRef(selectedRowsLocal);
  selectedRowsRef.current = selectedRowsLocal;
  useEffect(() => {
    if (selectedRows !== undefined) {
      setSelectedRowsLocal(selectedRows);
    }
  }, [selectedRows]);

  const handleRowSelectionChange = useCallback(
    (rowId: string, selected: boolean) => {
      const prev = selectedRowsRef.current;

      if (selected === prev.has(rowId)) {
        return;
      }
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(rowId);
      } else {
        newSet.delete(rowId);
      }

      setSelectedRowsLocal(newSet);
      if (onSelectionChange) {
        onSelectionChange(newSet);
      }
    },
    [onSelectionChange]
  );

  const handleAllSelectionChange = useCallback(() => {
    const prev = selectedRowsRef.current;
    const newSet = new Set(prev);

    const allVisibleSelected =
      visibleRowIds.size > 0 &&
      Array.from(visibleRowIds).every((id) => prev.has(id));

    if (allVisibleSelected) {
      visibleRowIds.forEach((id) => newSet.delete(id));
    } else {
      // Select all visible rows (keep existing selections from non-visible rows)
      visibleRowIds.forEach((id) => newSet.add(id));
    }

    setSelectedRowsLocal(newSet);
    if (onSelectionChange) {
      onSelectionChange(newSet);
    }
  }, [visibleRowIds]);

  const selectionColumn: DatagridColumn | null = useMemo(() => {
    if (!rowSelection) return null;

    return {
      field: "__selection__",
      headerName: "",
      renderHeader: () => {
        if (header) return header;
        if (!allowSelectAll || rowsForSelectAll.length === 0 || loading)
          return "";

        // Check if all / some visible rows are selected
        const selectedVisibleCount = Array.from(visibleRowIds).filter((id) =>
          selectedRowsLocal.has(id)
        ).length;
        const allVisibleSelected =
          visibleRowIds.size > 0 && selectedVisibleCount === visibleRowIds.size;
        const someVisibleSelected =
          selectedVisibleCount > 0 && !allVisibleSelected;

        return (
          <Tooltip title="Select all">
            <div
              style={{
                marginLeft: "-5px",
              }}
              onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={allVisibleSelected}
                indeterminate={someVisibleSelected}
                {...checkboxExtraProps}
                onChange={handleAllSelectionChange}
              />
            </div>
          </Tooltip>
        );
      },
      width: 48,
      sortable: true,
      filterable: false,
      align: "center",
      sortFn: (a: any, b: any) => {
        const aId = getRowId(a);
        const bId = getRowId(b);
        const aSelected = selectedRowsRef.current.has(aId) ? 1 : 0;
        const bSelected = selectedRowsRef.current.has(bId) ? 1 : 0;
        return aSelected - bSelected;
      },
      renderCell: (row: any) => {
        const rowId = getRowId(row);
        const isSelected = selectedRowsLocal.has(rowId);
        return (
          <Checkbox
            checked={isSelected}
            onChange={(checked) => handleRowSelectionChange(rowId, checked)}
            {...checkboxExtraProps}
          />
        );
      },
    };
  }, [
    rowSelection,
    handleRowSelectionChange,
    getRowId,
    selectedRowsLocal,
    checkboxExtraProps,
    allowSelectAll,
    rowsForSelectAll,
    loading,
    header,
    handleAllSelectionChange,
    visibleRowIds,
  ]);

  return {
    selectedRows: selectedRowsLocal,
    handleRowSelectionChange,
    selectionColumn,
  };
};
